import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  exportCsvUrl,
  useBulkLeadAction,
  useCustomFields,
  useLeadGroupMutations,
  useLeadGroups,
  useLeads,
  useMembers,
  useReorderLeads,
  useSavedViewMutations,
  useSavedViews,
  useStages,
  useTags,
  useUpdateLead,
} from '../api/queries';
import type { CustomField, Lead, LeadGroup, SavedView, Stage } from '@figureit/shared';
import { useAuth } from '../auth/AuthProvider';
import { useIsMobile } from '../hooks/useViewport';
import { daysSince, fmtDate, fmtILS, isOverdue, STALE_DAYS } from '../utils/format';
import { WonLostPrompt } from './WonLostPrompt';

type SortBy = 'manual' | string;

// Color presets offered when creating / recoloring a group.
const GROUP_COLORS = [
  '#6366f1',
  '#22c55e',
  '#ef4444',
  '#f59e0b',
  '#3b82f6',
  '#a855f7',
  '#ec4899',
  '#14b8a6',
  '#64748b',
];

const UNGROUPED_KEY = '__ungrouped__';
const sectionKey = (g: LeadGroup | null) => g?.id ?? UNGROUPED_KEY;

// Persist which group sections are collapsed, namespaced per org so workspaces
// don't share state. View-only UI state → localStorage rather than the server.
const collapseStorageKey = (orgId: string | null) =>
  orgId ? `figureit:collapsedGroups:${orgId}` : null;

const loadCollapsed = (key: string | null): Set<string> => {
  if (!key || typeof localStorage === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(key);
    return raw ? new Set<string>(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

const saveCollapsed = (key: string, set: Set<string>) => {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

// Muted placeholder for empty cells.
const Dash = () => <span className="text-text3">—</span>;

// Stage rendered as a filled status chip in the stage's own color. Uses a
// translucent tint + colored text so it stays readable on the dark theme for
// any user-chosen color.
function StagePill({ stage }: { stage?: Stage }) {
  if (!stage) return <Dash />;
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap"
      style={{
        background: `${stage.color}26`,
        color: stage.color,
        borderColor: `${stage.color}55`,
      }}
    >
      {stage.name}
    </span>
  );
}

export function ListView({ onSelectLead }: { onSelectLead: (id: string) => void }) {
  const { currentOrgId } = useAuth();
  const collapseKey = collapseStorageKey(currentOrgId);
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [staleOnly, setStaleOnly] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>('manual');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingTerminal, setPendingTerminal] = useState<{
    leadId: string;
    targetStage: Stage;
  } | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsed(collapseKey));
  const isMobile = useIsMobile();
  const [showFilters, setShowFilters] = useState(false);

  // Re-hydrate when the active org changes (the key changes with it), and persist
  // on every change so the collapsed/expanded layout survives reloads.
  useEffect(() => {
    setCollapsed(loadCollapsed(collapseKey));
  }, [collapseKey]);
  useEffect(() => {
    if (collapseKey) saveCollapsed(collapseKey, collapsed);
  }, [collapseKey, collapsed]);

  const stagesQ = useStages();
  const cfQ = useCustomFields();
  const tagsQ = useTags();
  const membersQ = useMembers();
  const groupsQ = useLeadGroups();
  const groupM = useLeadGroupMutations();
  const reorder = useReorderLeads();
  const update = useUpdateLead();
  const bulk = useBulkLeadAction();

  const stages = stagesQ.data?.items ?? [];
  const customFields = cfQ.data?.items ?? [];
  const visibleCfs = useMemo(
    () =>
      customFields
        .filter((f) => f.showInList !== false)
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [customFields],
  );
  const members = membersQ.data?.items ?? [];
  const groups = useMemo(
    () => (groupsQ.data?.items ?? []).slice().sort((a, b) => a.order - b.order),
    [groupsQ.data],
  );

  const leadsQ = useLeads({
    search,
    stageId: filterStage || undefined,
    priority: (filterPriority || undefined) as 'A' | 'B' | 'C' | undefined,
    ownerUserId: filterOwner || undefined,
    overdueOnly,
    staleOnly,
    sortBy: sortBy === 'manual' ? undefined : sortBy,
    sortDir: sortBy === 'manual' ? undefined : sortDir,
  });

  const leads = leadsQ.data?.items ?? [];
  const stageMap = useMemo(() => Object.fromEntries(stages.map((s) => [s.id, s])), [stages]);

  const isManual = sortBy === 'manual';
  const hasFilters =
    !!search || !!filterStage || !!filterPriority || !!filterOwner || overdueOnly || staleOnly;
  const canDrag = isManual && !hasFilters;

  // Partition leads into sections: each defined group (in order) + a trailing
  // "Ungrouped" bucket for leads with no groupId. Empty groups still render so
  // you can drag leads into them; the Ungrouped bucket is hidden once empty
  // (unless there are no groups at all).
  const sections = useMemo(() => {
    const byGroup = new Map<string, Lead[]>();
    for (const g of groups) byGroup.set(g.id, []);
    const ungrouped: Lead[] = [];
    for (const lead of leads) {
      if (lead.groupId && byGroup.has(lead.groupId)) byGroup.get(lead.groupId)!.push(lead);
      else ungrouped.push(lead);
    }
    const result: { group: LeadGroup | null; leads: Lead[] }[] = groups.map((g) => ({
      group: g,
      leads: byGroup.get(g.id) ?? [],
    }));
    if (ungrouped.length > 0 || groups.length === 0) {
      result.push({ group: null, leads: ungrouped });
    }
    return result;
  }, [groups, leads]);

  const toggleSort = (col: string) => {
    if (col === sortBy) {
      if (sortDir === 'desc') {
        setSortBy('manual');
        setSortDir('asc');
      } else setSortDir('desc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  // Drag-drop: track the dragged lead + the current drop target (section + index).
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ key: string; index: number } | null>(null);
  // Separate drag channel for reordering the group headers themselves.
  const [dragGroupId, setDragGroupId] = useState<string | null>(null);
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null);

  // Persist a group reorder: drop the dragged group into the target group's slot.
  const commitGroupReorder = (targetGroupId: string) => {
    if (!dragGroupId || dragGroupId === targetGroupId) {
      setDragGroupId(null);
      setDragOverGroupId(null);
      return;
    }
    const ids = groups.map((g) => g.id).filter((id) => id !== dragGroupId);
    const idx = ids.indexOf(targetGroupId);
    ids.splice(idx, 0, dragGroupId);
    groupM.reorder.mutate(ids);
    setDragGroupId(null);
    setDragOverGroupId(null);
  };

  // Persist a move/reorder: rebuild the target section's ordered id list with the
  // dragged lead inserted at `index`, then send it (with the target groupId) so the
  // server both repositions and re-assigns the group in one shot.
  const commitMove = (sectionLeads: Lead[], targetGroupId: string | null, index: number) => {
    if (!canDrag || !dragId) return;
    const ids = sectionLeads.map((l) => l.id).filter((id) => id !== dragId);
    const clamped = Math.max(0, Math.min(index, ids.length));
    ids.splice(clamped, 0, dragId);
    reorder.mutate({ ids, groupId: targetGroupId });
    setDragId(null);
    setDropTarget(null);
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === leads.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(leads.map((l) => l.id)));
  };

  const toggleCollapsed = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const ownerName = (id?: string | null) =>
    id ? (members.find((m) => m.userId === id)?.name ?? '—') : '—';

  const createGroup = () => {
    const name = prompt('New group name (e.g. "Closed Won", "Active deals"):')?.trim();
    if (!name) return;
    groupM.create.mutate({ name, color: GROUP_COLORS[groups.length % GROUP_COLORS.length] });
  };

  const totalCols = (canDrag ? 13 : 12) + visibleCfs.length;
  const activeFilterCount =
    (filterStage ? 1 : 0) +
    (filterPriority ? 1 : 0) +
    (filterOwner ? 1 : 0) +
    (overdueOnly ? 1 : 0) +
    (staleOnly ? 1 : 0);

  return (
    <>
      <div className="px-3 md:px-6 py-3 md:py-4 overflow-auto overscroll-contain h-full">
        <SavedViewsBar
          currentFilters={{
            search,
            stageId: filterStage || undefined,
            priority: (filterPriority || undefined) as 'A' | 'B' | 'C' | undefined,
            ownerUserId: filterOwner || undefined,
            overdueOnly,
            staleOnly,
          }}
          currentSort={{
            sortBy: sortBy === 'manual' ? null : sortBy,
            sortDir: sortBy === 'manual' ? null : sortDir,
          }}
          onApply={(v) => {
            setSearch(v.filters.search ?? '');
            setFilterStage(v.filters.stageId ?? '');
            setFilterPriority((v.filters.priority as 'A' | 'B' | 'C' | undefined) ?? '');
            setFilterOwner(v.filters.ownerUserId ?? '');
            setOverdueOnly(!!v.filters.overdueOnly);
            setStaleOnly(!!v.filters.staleOnly);
            setSortBy(v.sortBy ?? 'manual');
            setSortDir((v.sortDir as 'asc' | 'desc' | null) ?? 'asc');
          }}
        />
        <div className="flex gap-2 mb-3 flex-wrap items-center">
          <input
            type="search"
            placeholder="Search leads…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            enterKeyHint="search"
            autoCapitalize="none"
            className="flex-1 min-w-0 md:flex-none md:max-w-[260px]"
          />
          <button
            className={clsx(
              'btn btn-secondary md:hidden flex-shrink-0',
              (showFilters || activeFilterCount > 0) && 'border-accent text-accent2',
            )}
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
          >
            Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
          </button>
          {/* On phones the filters live in a collapsible 2-col panel; on desktop
              `md:contents` dissolves the wrapper so they flow inline as before. */}
          <div
            className={clsx(
              'w-full md:w-auto md:contents',
              showFilters ? 'grid grid-cols-2 gap-2' : 'hidden',
            )}
          >
            <select
              value={filterStage}
              onChange={(e) => setFilterStage(e.target.value)}
              className="md:max-w-[160px]"
            >
              <option value="">All Stages</option>
              {stages
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </select>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="md:max-w-[160px]"
            >
              <option value="">All Priorities</option>
              <option value="A">A — High</option>
              <option value="B">B — Medium</option>
              <option value="C">C — Low</option>
            </select>
            <select
              value={filterOwner}
              onChange={(e) => setFilterOwner(e.target.value)}
              className="md:max-w-[160px]"
            >
              <option value="">All Owners</option>
              {members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name ?? m.email}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 md:gap-1 text-sm md:text-xs text-text2 min-h-[44px] md:min-h-0 px-1 md:px-0">
              <input
                type="checkbox"
                checked={overdueOnly}
                onChange={(e) => setOverdueOnly(e.target.checked)}
                className="w-auto"
              />
              Overdue
            </label>
            <label className="flex items-center gap-2 md:gap-1 text-sm md:text-xs text-text2 min-h-[44px] md:min-h-0 px-1 md:px-0">
              <input
                type="checkbox"
                checked={staleOnly}
                onChange={(e) => setStaleOnly(e.target.checked)}
                className="w-auto"
              />
              Stale
            </label>
            {activeFilterCount > 0 && (
              <button
                className="btn btn-sm btn-ghost md:hidden col-span-2"
                onClick={() => {
                  setFilterStage('');
                  setFilterPriority('');
                  setFilterOwner('');
                  setOverdueOnly(false);
                  setStaleOnly(false);
                }}
              >
                Clear filters
              </button>
            )}
          </div>
          <span className="flex-1 hidden md:block" />
          <button className="btn btn-sm btn-secondary" onClick={createGroup}>
            + New group
          </button>
          <span className="text-xs text-text3">
            {leads.length} lead{leads.length !== 1 ? 's' : ''}
          </span>
          <a className="btn btn-sm btn-secondary hidden md:inline-flex" href={exportCsvUrl()}>
            Export CSV
          </a>
        </div>

        {!canDrag && groups.length > 0 && !isMobile && (
          <p className="text-[11px] text-text3 mb-2">
            Tip: clear filters and sorting (Sort: manual) to drag leads between groups.
          </p>
        )}

        {selectedIds.size > 0 && (
          <BulkBar
            count={selectedIds.size}
            onClear={() => setSelectedIds(new Set())}
            stages={stages}
            groups={groups}
            onAction={(payload) =>
              bulk.mutate(
                { ids: Array.from(selectedIds), ...payload },
                { onSuccess: () => setSelectedIds(new Set()) },
              )
            }
          />
        )}

        {isMobile ? (
          <MobileLeadCards
            sections={sections}
            stageMap={stageMap}
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
            selectedIds={selectedIds}
            onToggleSelected={toggleSelected}
            onSelectLead={onSelectLead}
            ownerName={ownerName}
            onRenameGroup={(g, name) => groupM.update.mutate({ id: g.id, name })}
            onDeleteGroup={(g, count) => {
              if (
                confirm(
                  `Delete group "${g.name}"? Its ${count} lead(s) move to Ungrouped — they are not deleted.`,
                )
              )
                groupM.remove.mutate(g.id);
            }}
          />
        ) : (
          <div>
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className="bg-surface px-2 py-2.5 w-8">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === leads.length && leads.length > 0}
                      onChange={selectAll}
                      className="w-auto"
                    />
                  </th>
                  {canDrag && <th className="bg-surface w-8 px-1" />}
                  <SortHeader col="company" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>
                    Company
                  </SortHeader>
                  <SortHeader
                    col="contactName"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onClick={toggleSort}
                  >
                    Contact
                  </SortHeader>
                  <SortHeader col="stage" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>
                    Stage
                  </SortHeader>
                  <SortHeader
                    col="nreValue"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  >
                    NRE
                  </SortHeader>
                  <SortHeader
                    col="dealValueYearly"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onClick={toggleSort}
                    align="right"
                  >
                    Yearly
                  </SortHeader>
                  <SortHeader col="priority" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>
                    Priority
                  </SortHeader>
                  <SortHeader
                    col="ownerUserId"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onClick={toggleSort}
                  >
                    Owner
                  </SortHeader>
                  <SortHeader
                    col="nextAction"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onClick={toggleSort}
                  >
                    Next Action
                  </SortHeader>
                  <SortHeader
                    col="nextActionDate"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onClick={toggleSort}
                  >
                    Due
                  </SortHeader>
                  <SortHeader
                    col="lastActivityAt"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onClick={toggleSort}
                  >
                    Last Touch
                  </SortHeader>
                  {visibleCfs.map((f) => (
                    <th
                      key={f.id}
                      className="bg-surface px-3 py-2.5 text-left text-xs text-text2 font-semibold"
                    >
                      {f.label}
                    </th>
                  ))}
                  <th className="bg-surface w-9" />
                </tr>
              </thead>
              {sections.map((section, sIdx) => {
                const key = sectionKey(section.group);
                const groupId = section.group?.id ?? null;
                const isCollapsed = collapsed.has(key);
                const subtotal = section.leads.reduce((s, l) => s + (l.dealValueYearly ?? 0), 0);
                // Group accent color drives the left "spine" that ties each box together.
                const accent = section.group?.color ?? '#94a3b8';
                return (
                  <tbody key={key} className="align-middle">
                    {sIdx > 0 && (
                      <tr aria-hidden className="bg-transparent">
                        <td colSpan={totalCols} className="h-3 p-0 border-0" />
                      </tr>
                    )}
                    <GroupHeaderRow
                      group={section.group}
                      count={section.leads.length}
                      subtotal={subtotal}
                      totalCols={totalCols}
                      collapsed={isCollapsed}
                      onToggle={() => toggleCollapsed(key)}
                      isLeadDropTarget={dropTarget?.key === key && dropTarget.index === 0}
                      isGroupDropTarget={
                        !!section.group &&
                        dragOverGroupId === section.group.id &&
                        dragGroupId !== section.group.id
                      }
                      dragging={!!section.group && dragGroupId === section.group.id}
                      onHandleDragStart={() => section.group && setDragGroupId(section.group.id)}
                      onRowDragOver={(e) => {
                        if (dragGroupId && section.group) {
                          e.preventDefault();
                          setDragOverGroupId(section.group.id);
                        } else if (canDrag && dragId) {
                          e.preventDefault();
                          setDropTarget({ key, index: 0 });
                        }
                      }}
                      onRowDrop={(e) => {
                        e.preventDefault();
                        if (dragGroupId && section.group) commitGroupReorder(section.group.id);
                        else if (dragId) commitMove(section.leads, groupId, 0);
                      }}
                      onRowDragEnd={() => {
                        setDragGroupId(null);
                        setDragOverGroupId(null);
                      }}
                      onRename={(name) =>
                        section.group && groupM.update.mutate({ id: section.group.id, name })
                      }
                      onRecolor={(color) =>
                        section.group && groupM.update.mutate({ id: section.group.id, color })
                      }
                      onDelete={() => {
                        if (!section.group) return;
                        if (
                          confirm(
                            `Delete group "${section.group.name}"? Its ${section.leads.length} lead(s) move to Ungrouped — they are not deleted.`,
                          )
                        )
                          groupM.remove.mutate(section.group.id);
                      }}
                    />
                    {!isCollapsed &&
                      section.leads.map((lead, idx) => {
                        const stage = stageMap[lead.stageId];
                        const overdue = isOverdue(lead.nextActionDate);
                        const isLast = idx === section.leads.length - 1;
                        return (
                          <tr
                            key={lead.id}
                            className={clsx(
                              'group transition-colors hover:bg-surface2',
                              '[&>td]:border-b [&>td]:border-border',
                              overdue && 'bg-red/5',
                              dragId === lead.id && 'opacity-40',
                              dropTarget?.key === key &&
                                dropTarget.index === idx &&
                                '[&>td]:border-t-2 [&>td]:border-t-accent',
                            )}
                            draggable={canDrag}
                            onDragStart={() => canDrag && setDragId(lead.id)}
                            onDragOver={(e) => {
                              if (!canDrag || !dragId) return;
                              e.preventDefault();
                              setDropTarget({ key, index: idx });
                            }}
                            onDragLeave={() => setDropTarget(null)}
                            onDrop={(e) => {
                              e.preventDefault();
                              commitMove(section.leads, groupId, idx);
                            }}
                            onDragEnd={() => {
                              setDragId(null);
                              setDropTarget(null);
                            }}
                          >
                            <td
                              className={clsx('px-2 py-2.5 text-center', isLast && 'rounded-bl-lg')}
                              style={{ borderLeft: `4px solid ${accent}` }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedIds.has(lead.id)}
                                onChange={() => toggleSelected(lead.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="w-auto"
                              />
                            </td>
                            {canDrag && (
                              <td className="px-1 text-center select-none">
                                <span className="cursor-grab text-text3 opacity-0 group-hover:opacity-100 transition-opacity">
                                  ⠿
                                </span>
                              </td>
                            )}
                            <EditableCell
                              lead={lead}
                              field="company"
                              type="text"
                              stages={stages}
                              members={members}
                            />
                            <EditableCell
                              lead={lead}
                              field="contactName"
                              type="text"
                              stages={stages}
                              members={members}
                            />
                            <EditableCell
                              lead={lead}
                              field="stageId"
                              type="select-stage"
                              stages={stages}
                              members={members}
                              onTerminalStage={(targetStage) =>
                                setPendingTerminal({ leadId: lead.id, targetStage })
                              }
                              display={<StagePill stage={stage} />}
                            />
                            <EditableCell
                              lead={lead}
                              field="nreValue"
                              type="number"
                              stages={stages}
                              members={members}
                              tdClassName="text-right tabular-nums"
                              display={lead.nreValue ? fmtILS(lead.nreValue) : <Dash />}
                            />
                            <EditableCell
                              lead={lead}
                              field="dealValueYearly"
                              type="number"
                              stages={stages}
                              members={members}
                              tdClassName="text-right tabular-nums"
                              display={
                                lead.dealValueYearly ? (
                                  <span className="text-green font-medium">
                                    {fmtILS(lead.dealValueYearly)}
                                  </span>
                                ) : (
                                  <Dash />
                                )
                              }
                            />
                            <EditableCell
                              lead={lead}
                              field="priority"
                              type="select-priority"
                              stages={stages}
                              members={members}
                              display={
                                <span
                                  className={`badge badge-${(lead.priority || 'B').toLowerCase()}`}
                                >
                                  {lead.priority || 'B'}
                                </span>
                              }
                            />
                            <EditableCell
                              lead={lead}
                              field="ownerUserId"
                              type="select-owner"
                              stages={stages}
                              members={members}
                              display={lead.ownerUserId ? ownerName(lead.ownerUserId) : <Dash />}
                            />
                            <EditableCell
                              lead={lead}
                              field="nextAction"
                              type="text"
                              stages={stages}
                              members={members}
                            />
                            <EditableCell
                              lead={lead}
                              field="nextActionDate"
                              type="date"
                              stages={stages}
                              members={members}
                              display={
                                lead.nextActionDate ? (
                                  <span className={overdue ? 'text-red' : ''}>
                                    {fmtDate(lead.nextActionDate)}
                                  </span>
                                ) : (
                                  <Dash />
                                )
                              }
                            />
                            <EditableCell
                              lead={lead}
                              field="lastActivityAt"
                              type="date"
                              stages={stages}
                              members={members}
                              display={
                                <span
                                  className={
                                    daysSince(lead.lastActivityAt) >= STALE_DAYS
                                      ? 'text-red'
                                      : 'text-text3'
                                  }
                                >
                                  {fmtDate(lead.lastActivityAt)}
                                </span>
                              }
                            />
                            {visibleCfs.map((f) => (
                              <CustomFieldCell key={f.id} lead={lead} field={f} />
                            ))}
                            <td
                              className={clsx(
                                'px-1 text-center border-r border-border',
                                isLast && 'rounded-br-lg',
                              )}
                            >
                              <button
                                className="text-text3 hover:text-accent2 text-base"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSelectLead(lead.id);
                                }}
                                title="Open detail"
                              >
                                ›
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    {!isCollapsed && section.leads.length === 0 && (
                      <tr>
                        <td
                          colSpan={totalCols}
                          className="text-center text-text3 py-5 text-xs italic border-b border-r border-border rounded-b-lg"
                          style={{ borderLeft: `4px solid ${accent}` }}
                        >
                          {section.group ? 'Empty group — drag leads here' : 'No leads'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                );
              })}
              {leads.length === 0 && (
                <tbody>
                  <tr>
                    <td colSpan={totalCols} className="text-center text-text3 py-8">
                      No leads found
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        )}
      </div>
      {pendingTerminal && (
        <WonLostPrompt
          leadId={pendingTerminal.leadId}
          targetStage={pendingTerminal.targetStage}
          onClose={() => setPendingTerminal(null)}
          onDone={() => setPendingTerminal(null)}
        />
      )}
    </>
  );
}

function GroupHeaderRow({
  group,
  count,
  subtotal,
  totalCols,
  collapsed,
  onToggle,
  isLeadDropTarget,
  isGroupDropTarget,
  dragging,
  onHandleDragStart,
  onRowDragOver,
  onRowDrop,
  onRowDragEnd,
  onRename,
  onRecolor,
  onDelete,
}: {
  group: LeadGroup | null;
  count: number;
  subtotal: number;
  totalCols: number;
  collapsed: boolean;
  onToggle: () => void;
  isLeadDropTarget: boolean;
  isGroupDropTarget: boolean;
  dragging: boolean;
  onHandleDragStart: () => void;
  onRowDragOver: (e: React.DragEvent) => void;
  onRowDrop: (e: React.DragEvent) => void;
  onRowDragEnd: () => void;
  onRename: (name: string) => void;
  onRecolor: (color: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group?.name ?? '');
  const [showColors, setShowColors] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const color = group?.color ?? '#94a3b8';

  useEffect(() => setName(group?.name ?? ''), [group?.name]);

  const commitName = () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== group?.name) onRename(trimmed);
    else setName(group?.name ?? '');
    setEditing(false);
  };

  return (
    <tr
      className={clsx(
        'group/hdr select-none',
        (isLeadDropTarget || isGroupDropTarget) && 'outline outline-2 outline-accent',
        dragging && 'opacity-50',
      )}
      onDragOver={onRowDragOver}
      onDrop={onRowDrop}
      onDragEnd={onRowDragEnd}
    >
      <td
        colSpan={totalCols}
        className={clsx(
          'sticky top-[42px] z-[5] border-y border-r border-border px-2 py-3 shadow-sm',
          collapsed ? 'rounded-lg' : 'rounded-t-lg',
        )}
        style={{ background: `${color}20`, borderLeft: `4px solid ${color}` }}
      >
        <div className="flex items-center gap-2.5">
          {group && (
            <span
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                onHandleDragStart();
              }}
              className="cursor-grab text-text3 hover:text-text select-none text-xs leading-none opacity-0 group-hover/hdr:opacity-100 transition-opacity"
              title="Drag to reorder group"
            >
              ⠿
            </span>
          )}
          <button
            onClick={onToggle}
            className="text-text3 hover:text-text w-4 text-center"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? '▸' : '▾'}
          </button>
          <span className="relative">
            <button
              className="w-3 h-3 rounded-sm border border-black/10 block"
              style={{ background: color }}
              onClick={() => group && setShowColors((s) => !s)}
              title={group ? 'Change color' : undefined}
            />
            {showColors && group && (
              <span className="absolute z-20 top-5 left-0 flex gap-1 bg-surface border border-border rounded-md p-1 shadow-lg">
                {GROUP_COLORS.map((c) => (
                  <button
                    key={c}
                    className="w-4 h-4 rounded-sm border border-black/10"
                    style={{ background: c }}
                    onClick={() => {
                      onRecolor(c);
                      setShowColors(false);
                    }}
                  />
                ))}
              </span>
            )}
          </span>
          {editing && group ? (
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitName();
                if (e.key === 'Escape') {
                  setName(group?.name ?? '');
                  setEditing(false);
                }
              }}
              className="text-[15px] font-bold max-w-[220px] py-0.5"
            />
          ) : (
            <button
              className={clsx(
                'text-[15px] font-bold tracking-tight',
                group ? 'text-text hover:text-accent2' : 'text-text3 italic font-semibold',
              )}
              onClick={() => group && setEditing(true)}
              title={group ? 'Rename group' : undefined}
            >
              {group?.name ?? 'Ungrouped'}
            </button>
          )}
          <span className="text-xs text-text3">
            {count} lead{count !== 1 ? 's' : ''}
          </span>
          {subtotal > 0 && <span className="text-xs text-green">{fmtILS(subtotal)}/yr</span>}
          <span className="flex-1" />
          {group && (
            <span
              className={clsx(
                'relative transition-opacity',
                showMenu ? 'opacity-100' : 'opacity-0 group-hover/hdr:opacity-100',
              )}
            >
              <button
                className="text-text3 hover:text-text px-1 text-base leading-none"
                onClick={() => setShowMenu((s) => !s)}
                onBlur={() => setTimeout(() => setShowMenu(false), 120)}
                title="Group actions"
                aria-label="Group actions"
              >
                ⋯
              </button>
              {showMenu && (
                <span className="absolute z-20 top-6 right-0 min-w-[130px] bg-surface border border-border rounded-md py-1 shadow-lg flex flex-col text-xs">
                  <button
                    className="px-3 py-1.5 text-left hover:bg-surface2"
                    onClick={() => {
                      setShowMenu(false);
                      setEditing(true);
                    }}
                  >
                    Rename
                  </button>
                  <button
                    className="px-3 py-1.5 text-left hover:bg-surface2 text-red"
                    onClick={() => {
                      setShowMenu(false);
                      onDelete();
                    }}
                  >
                    Delete
                  </button>
                </span>
              )}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function SortHeader({
  col,
  sortBy,
  sortDir,
  onClick,
  children,
  align = 'left',
}: {
  col: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onClick: (c: string) => void;
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      onClick={() => onClick(col)}
      className={clsx(
        'bg-surface px-3 py-2.5 text-xs font-semibold text-text2 cursor-pointer whitespace-nowrap select-none hover:text-text',
        align === 'right' ? 'text-right' : 'text-left',
        sortBy === col && 'text-accent2',
      )}
    >
      {children}
      {sortBy === col && <span className="ml-1 text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
}

function BulkBar({
  count,
  onClear,
  stages,
  groups,
  onAction,
}: {
  count: number;
  onClear: () => void;
  stages: Stage[];
  groups: LeadGroup[];
  onAction: (payload: {
    action: 'set-stage' | 'set-owner' | 'set-priority' | 'set-group' | 'add-tags' | 'delete';
    stageId?: string;
    priority?: 'A' | 'B' | 'C';
    groupId?: string | null;
  }) => void;
}) {
  return (
    <div className="bg-accent/10 border border-accent/40 rounded-md p-2 mb-3 flex flex-wrap items-center gap-2 text-sm">
      <span className="text-accent2 font-medium w-full md:w-auto flex items-center justify-between">
        {count} selected
        <button className="btn btn-sm btn-ghost md:hidden" onClick={onClear}>
          Clear
        </button>
      </span>
      <select
        defaultValue=""
        className="flex-1 min-w-[140px] md:flex-none"
        onChange={(e) => {
          if (e.target.value) onAction({ action: 'set-stage', stageId: e.target.value });
          e.target.value = '';
        }}
      >
        <option value="">Move to stage…</option>
        {stages
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
      </select>
      <select
        defaultValue=""
        className="flex-1 min-w-[140px] md:flex-none"
        onChange={(e) => {
          const v = e.target.value;
          if (v) onAction({ action: 'set-group', groupId: v === '__none__' ? null : v });
          e.target.value = '';
        }}
      >
        <option value="">Move to group…</option>
        {groups
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        <option value="__none__">— Ungrouped —</option>
      </select>
      <select
        defaultValue=""
        className="flex-1 min-w-[140px] md:flex-none"
        onChange={(e) => {
          if (e.target.value)
            onAction({ action: 'set-priority', priority: e.target.value as 'A' | 'B' | 'C' });
          e.target.value = '';
        }}
      >
        <option value="">Set priority…</option>
        <option value="A">A</option>
        <option value="B">B</option>
        <option value="C">C</option>
      </select>
      <button
        className="btn btn-sm btn-danger"
        onClick={() => {
          if (confirm(`Delete ${count} leads?`)) onAction({ action: 'delete' });
        }}
      >
        Delete
      </button>
      <span className="flex-1 hidden md:block" />
      <button className="btn btn-sm btn-ghost hidden md:inline-flex" onClick={onClear}>
        Clear
      </button>
    </div>
  );
}

function EditableCell({
  lead,
  field,
  type,
  display,
  stages,
  members,
  onTerminalStage,
  tdClassName,
}: {
  lead: Lead;
  field: string;
  type: 'text' | 'number' | 'date' | 'select-stage' | 'select-priority' | 'select-owner';
  display?: React.ReactNode;
  stages: Stage[];
  members: { userId: string; email: string; name: string | null }[];
  onTerminalStage?: (targetStage: Stage) => void;
  tdClassName?: string;
}) {
  const update = useUpdateLead();
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState<string>('');
  const ref = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

  useEffect(() => {
    if (editing && ref.current) ref.current.focus();
  }, [editing]);

  const startEdit = () => {
    const cur = (lead as unknown as Record<string, unknown>)[field];
    if (cur == null) setVal('');
    else if (typeof cur === 'number') setVal(String(cur));
    // Date inputs need YYYY-MM-DD; some fields (e.g. lastActivityAt) are full ISO
    // datetimes, so take just the date prefix.
    else if (type === 'date') setVal(String(cur).slice(0, 10));
    else setVal(String(cur));
    setEditing(true);
  };

  const commit = (raw?: string) => {
    const v = raw ?? val;
    let toSave: unknown = v;
    if (type === 'number') toSave = v === '' ? null : Number(v);
    if (type === 'date') toSave = v === '' ? null : v;
    if (type === 'select-owner') toSave = v === '' ? null : v;
    if (type === 'select-stage' && typeof v === 'string' && v !== lead.stageId) {
      const next = stages.find((s) => s.id === v);
      if (next && (next.isWon || next.isLost) && onTerminalStage) {
        onTerminalStage(next);
        setEditing(false);
        return;
      }
    }
    update.mutate({ id: lead.id, [field]: toSave } as Parameters<typeof update.mutate>[0]);
    setEditing(false);
  };

  if (!editing) {
    const raw = (lead as unknown as Record<string, unknown>)[field];
    return (
      <td className={clsx('px-3 py-2.5 cursor-text', tdClassName)} onDoubleClick={startEdit}>
        {display ?? (raw as React.ReactNode) ?? <Dash />}
      </td>
    );
  }

  if (type === 'select-stage') {
    return (
      <td className="px-3 py-2.5">
        <select
          ref={ref as React.RefObject<HTMLSelectElement>}
          value={val}
          onChange={(e) => commit(e.target.value)}
          onBlur={() => setEditing(false)}
          className="text-xs"
        >
          {stages
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </select>
      </td>
    );
  }
  if (type === 'select-priority') {
    return (
      <td className="px-3 py-2.5">
        <select
          ref={ref as React.RefObject<HTMLSelectElement>}
          value={val}
          onChange={(e) => commit(e.target.value)}
          onBlur={() => setEditing(false)}
          className="text-xs"
        >
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
        </select>
      </td>
    );
  }
  if (type === 'select-owner') {
    return (
      <td className="px-3 py-2.5">
        <select
          ref={ref as React.RefObject<HTMLSelectElement>}
          value={val}
          onChange={(e) => commit(e.target.value)}
          onBlur={() => setEditing(false)}
          className="text-xs"
        >
          <option value="">—</option>
          {members.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name ?? m.email}
            </option>
          ))}
        </select>
      </td>
    );
  }
  return (
    <td className="px-3 py-2.5">
      <input
        ref={ref as React.RefObject<HTMLInputElement>}
        type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="text-xs"
      />
    </td>
  );
}

function CustomFieldCell({ lead, field }: { lead: Lead; field: CustomField }) {
  const update = useUpdateLead();
  const [editing, setEditing] = useState(false);
  const value = (lead.customFields ?? {})[field.id] ?? '';
  const [val, setVal] = useState<string>('');
  const ref = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

  useEffect(() => {
    if (editing && ref.current) ref.current.focus();
  }, [editing]);

  const startEdit = () => {
    setVal(value == null ? '' : String(value));
    setEditing(true);
  };

  const commit = (raw?: string) => {
    const next = raw ?? val;
    let v: unknown = next;
    if (field.type === 'number') v = next === '' ? '' : Number(next);
    update.mutate({
      id: lead.id,
      customFields: { ...(lead.customFields ?? {}), [field.id]: v as never },
    });
    setEditing(false);
  };

  if (!editing) {
    let display: React.ReactNode = value;
    if (value === '' || value == null) display = <span className="text-text3">—</span>;
    else if (field.type === 'date') display = fmtDate(String(value));
    else if (field.type === 'select')
      display = <span className="badge bg-surface3 text-text">{String(value)}</span>;
    else if (field.type === 'url' && value)
      display = (
        <a
          href={String(value)}
          target="_blank"
          rel="noreferrer"
          className="text-accent2"
          onClick={(e) => e.stopPropagation()}
        >
          {String(value)}
        </a>
      );
    return (
      <td
        className={clsx(
          'px-3 py-2.5 text-text2 cursor-text',
          field.type === 'number' && 'text-right tabular-nums',
        )}
        onDoubleClick={startEdit}
      >
        {display}
      </td>
    );
  }

  if (field.type === 'select') {
    return (
      <td className="px-3 py-2.5">
        <select
          ref={ref as React.RefObject<HTMLSelectElement>}
          value={val}
          onChange={(e) => commit(e.target.value)}
          onBlur={() => setEditing(false)}
        >
          <option value="">—</option>
          {(field.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </td>
    );
  }
  return (
    <td className="px-3 py-2.5">
      <input
        ref={ref as React.RefObject<HTMLInputElement>}
        type={
          field.type === 'date'
            ? 'date'
            : field.type === 'number'
              ? 'number'
              : field.type === 'url'
                ? 'url'
                : field.type === 'email'
                  ? 'email'
                  : 'text'
        }
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
      />
    </td>
  );
}

function SavedViewsBar({
  currentFilters,
  currentSort,
  onApply,
}: {
  currentFilters: SavedView['filters'];
  currentSort: { sortBy: SavedView['sortBy']; sortDir: SavedView['sortDir'] };
  onApply: (v: SavedView) => void;
}) {
  const { data } = useSavedViews();
  const m = useSavedViewMutations();
  const [activeId, setActiveId] = useState<string | null>(null);
  const views = data?.items ?? [];

  const apply = (v: SavedView) => {
    setActiveId(v.id);
    onApply(v);
  };

  const saveCurrent = () => {
    const name = prompt('Name this view (e.g. "My hot leads"):')?.trim();
    if (!name) return;
    const shared = confirm(
      'Share this view with your whole organization?\nOK = shared, Cancel = just for me',
    );
    m.create.mutate(
      {
        name,
        filters: currentFilters,
        sortBy: currentSort.sortBy,
        sortDir: currentSort.sortDir,
        isShared: shared,
      },
      {
        onSuccess: (v) => setActiveId(v.id),
      },
    );
  };

  return (
    <div className="flex flex-nowrap md:flex-wrap overflow-x-auto no-scrollbar -mx-3 px-3 md:mx-0 md:px-0 items-center gap-1.5 mb-2">
      <span className="text-[11px] text-text3 mr-1 flex-shrink-0">Views:</span>
      {views.length === 0 && <span className="text-[11px] text-text3">none yet —</span>}
      {views.map((v) => (
        <span
          key={v.id}
          className="inline-flex items-center bg-surface2 border border-border rounded-md overflow-hidden flex-shrink-0"
        >
          <button
            className={
              'px-2.5 py-1 text-xs ' +
              (activeId === v.id ? 'bg-accent text-white' : 'hover:bg-surface3')
            }
            onClick={() => apply(v)}
            title={v.isShared ? 'Shared with org' : 'Private to you'}
          >
            {v.isShared ? '🌐 ' : ''}
            {v.name}
          </button>
          <button
            className="px-1.5 text-text3 hover:text-red text-sm border-l border-border"
            onClick={() => {
              if (confirm(`Delete view "${v.name}"?`)) m.remove.mutate(v.id);
            }}
            title="Remove view"
          >
            ×
          </button>
        </span>
      ))}
      <button
        className="btn btn-sm btn-ghost text-[11px] flex-shrink-0 whitespace-nowrap"
        onClick={saveCurrent}
      >
        + Save current as view
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------------ */
/* Phone layout: one card per lead instead of a 12-column table. Tap a card  */
/* to open the detail sheet (where stage/next-action/etc. are edited); the   */
/* checkbox feeds the same bulk-action bar as the table.                     */
/* ------------------------------------------------------------------------ */
function MobileLeadCards({
  sections,
  stageMap,
  collapsed,
  onToggleCollapsed,
  selectedIds,
  onToggleSelected,
  onSelectLead,
  ownerName,
  onRenameGroup,
  onDeleteGroup,
}: {
  sections: { group: LeadGroup | null; leads: Lead[] }[];
  stageMap: Record<string, Stage>;
  collapsed: Set<string>;
  onToggleCollapsed: (key: string) => void;
  selectedIds: Set<string>;
  onToggleSelected: (id: string) => void;
  onSelectLead: (id: string) => void;
  ownerName: (id?: string | null) => string;
  onRenameGroup: (g: LeadGroup, name: string) => void;
  onDeleteGroup: (g: LeadGroup, count: number) => void;
}) {
  const total = sections.reduce((n, s) => n + s.leads.length, 0);
  if (total === 0 && sections.every((s) => !s.group)) {
    return <div className="text-center text-text3 py-10 text-sm">No leads found</div>;
  }
  return (
    <div className="space-y-4">
      {sections.map((section) => {
        const key = sectionKey(section.group);
        const isCollapsed = collapsed.has(key);
        const subtotal = section.leads.reduce((s, l) => s + (l.dealValueYearly ?? 0), 0);
        const accent = section.group?.color ?? '#94a3b8';
        return (
          <div key={key}>
            <div
              className="flex items-center gap-2 rounded-lg px-2.5 py-2 mb-2 border border-border"
              style={{ background: `${accent}20`, borderLeft: `4px solid ${accent}` }}
            >
              <button
                className="flex-1 min-w-0 flex items-center gap-2 text-left min-h-[36px]"
                onClick={() => onToggleCollapsed(key)}
                aria-expanded={!isCollapsed}
              >
                <span className="text-text3 w-3 text-center">{isCollapsed ? '▸' : '▾'}</span>
                <span
                  className={clsx(
                    'font-bold text-[15px] truncate',
                    section.group ? 'text-text' : 'text-text3 italic',
                  )}
                >
                  {section.group?.name ?? 'Ungrouped'}
                </span>
                <span className="text-xs text-text3 flex-shrink-0">{section.leads.length}</span>
                {subtotal > 0 && (
                  <span className="text-xs text-green flex-shrink-0">{fmtILS(subtotal)}/yr</span>
                )}
              </button>
              {section.group && (
                <GroupMenuButton
                  onRename={() => {
                    const name = prompt('Rename group:', section.group!.name)?.trim();
                    if (name && name !== section.group!.name) onRenameGroup(section.group!, name);
                  }}
                  onDelete={() => onDeleteGroup(section.group!, section.leads.length)}
                />
              )}
            </div>
            {!isCollapsed && section.leads.length === 0 && (
              <div className="text-center text-text3 py-4 text-xs italic">
                {section.group ? 'Empty group' : 'No leads'}
              </div>
            )}
            {!isCollapsed && (
              <div className="space-y-2">
                {section.leads.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    stage={stageMap[lead.stageId]}
                    selected={selectedIds.has(lead.id)}
                    onToggleSelected={() => onToggleSelected(lead.id)}
                    onOpen={() => onSelectLead(lead.id)}
                    ownerName={ownerName(lead.ownerUserId)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function GroupMenuButton({ onRename, onDelete }: { onRename: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative flex-shrink-0">
      <button
        className="text-text3 hover:text-text px-2 min-h-[36px] text-lg leading-none"
        onClick={() => setOpen((v) => !v)}
        aria-label="Group actions"
      >
        ⋯
      </button>
      {open && (
        <>
          <span className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <span className="absolute z-20 top-9 right-0 min-w-[140px] bg-surface border border-border rounded-md py-1 shadow-lg flex flex-col text-sm">
            <button
              className="px-3 py-2.5 text-left hover:bg-surface2"
              onClick={() => {
                setOpen(false);
                onRename();
              }}
            >
              Rename
            </button>
            <button
              className="px-3 py-2.5 text-left hover:bg-surface2 text-red"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            >
              Delete group
            </button>
          </span>
        </>
      )}
    </span>
  );
}

function LeadCard({
  lead,
  stage,
  selected,
  onToggleSelected,
  onOpen,
  ownerName,
}: {
  lead: Lead;
  stage?: Stage;
  selected: boolean;
  onToggleSelected: () => void;
  onOpen: () => void;
  ownerName: string;
}) {
  const overdue = isOverdue(lead.nextActionDate);
  const stale = daysSince(lead.lastActivityAt) >= STALE_DAYS;
  return (
    <div
      className={clsx(
        'bg-surface border rounded-lg flex items-stretch',
        selected ? 'border-accent bg-accent/5' : overdue ? 'border-red/40' : 'border-border',
      )}
    >
      <label
        className="flex items-center justify-center pl-3 pr-2 cursor-pointer"
        onClick={(e) => e.stopPropagation()}
      >
        <input type="checkbox" checked={selected} onChange={onToggleSelected} className="w-auto" />
      </label>
      <button
        className="flex-1 min-w-0 text-left py-3 pr-3 active:bg-surface2 rounded-r-lg"
        onClick={onOpen}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-[15px] leading-tight truncate">{lead.company}</div>
            <div className="text-xs text-text2 truncate mt-0.5">
              {lead.contactName || '—'}
              {lead.contactTitle ? ` · ${lead.contactTitle}` : ''}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className={`badge badge-${(lead.priority || 'B').toLowerCase()}`}>
              {lead.priority || 'B'}
            </span>
            <span className="text-text3 text-lg leading-none">›</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <StagePill stage={stage} />
          {lead.dealValueYearly ? (
            <span className="text-xs text-green font-medium tabular-nums">
              {fmtILS(lead.dealValueYearly)}/yr
            </span>
          ) : null}
          {lead.nreValue ? (
            <span className="text-xs text-text2 tabular-nums">NRE {fmtILS(lead.nreValue)}</span>
          ) : null}
          {lead.ownerUserId && (
            <span className="text-[11px] text-text3 truncate ml-auto">{ownerName}</span>
          )}
        </div>
        {(lead.nextAction || lead.nextActionDate || stale) && (
          <div className="mt-2 text-xs flex items-center gap-2 min-w-0">
            {(lead.nextAction || lead.nextActionDate) && (
              <span className={clsx('truncate', overdue ? 'text-red' : 'text-text2')}>
                {overdue ? '⏰ ' : '→ '}
                {lead.nextAction || 'Next action'}
                {lead.nextActionDate ? ` · ${fmtDate(lead.nextActionDate)}` : ''}
              </span>
            )}
            {stale && (
              <span className="text-orange flex-shrink-0 ml-auto">
                ⚠ {daysSince(lead.lastActivityAt)}d quiet
              </span>
            )}
          </div>
        )}
      </button>
    </div>
  );
}
